import * as React from 'react'
import { CalendarIcon } from 'lucide-react'

import { DATE_PRESETS, dateFilterLabel, formatUtcDateLabel, normalizeCalendarDateToUtc, utcToday } from './dateRange'
import type { DateFilter, DatePreset } from './types'

import { Button } from '@/components/ui/button'
import { CalendarField } from '@/components/ui/calendar-field'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface DateRangeFilterProps {
  value: DateFilter
  onChange: (next: DateFilter) => void
  disabled?: boolean
  // Applied to the trigger Button — lets parents stretch the pill to fill a grid cell.
  triggerClassName?: string
}

const isAfterToday = (d: Date) => normalizeCalendarDateToUtc(d).getTime() > utcToday().getTime()

const DateRangeFilter = ({ value, onChange, disabled = false, triggerClassName }: DateRangeFilterProps) => {
  const [open, setOpen] = React.useState(false)

  const customStart = value.kind === 'custom' ? value.start : undefined
  const customEnd = value.kind === 'custom' ? value.end : undefined

  const selectPreset = (preset: DatePreset) => {
    onChange({ kind: 'preset', preset })
    setOpen(false)
  }

  const setCustomStart = (d: Date) => {
    const end = customEnd ?? d
    onChange({ kind: 'custom', start: d, end: d > end ? d : end })
  }

  const setCustomEnd = (d: Date) => {
    const start = customStart ?? d
    onChange({ kind: 'custom', start: start > d ? d : start, end: d })
  }

  const activePreset = value.kind === 'preset' ? value.preset : null

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setOpen(false)
          return
        }
        if (!disabled) setOpen(true)
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="lg" disabled={disabled} className={cn(triggerClassName)}>
          <CalendarIcon className="size-[22px]" />
          <span className="flex-1 truncate text-left">{dateFilterLabel(value)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[564px] p-0" align="start">
        <div className="grid grid-cols-2">
          <div className="border-r p-1">
            <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium">Date range</div>
            <div className="bg-border mx-1 h-px" />
            <div className="flex flex-col gap-0.5 pt-1">
              {DATE_PRESETS.map((preset) => {
                const isActive = activePreset === preset.value
                return (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => selectPreset(preset.value)}
                    className={cn(
                      'hover:bg-accent rounded-md px-2 py-1.5 text-left text-sm',
                      isActive && 'bg-muted font-semibold'
                    )}
                  >
                    {preset.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="flex flex-col gap-3 px-6 py-4">
            <CalendarField
              label="Start date"
              selected={customStart}
              placeholder="Pick a date"
              formatLabel={formatUtcDateLabel}
              isDisabled={isAfterToday}
              onSelect={(d) => setCustomStart(normalizeCalendarDateToUtc(d))}
            />
            <CalendarField
              label="End date"
              selected={customEnd}
              placeholder="Pick a date"
              formatLabel={formatUtcDateLabel}
              isDisabled={isAfterToday}
              onSelect={(d) => setCustomEnd(normalizeCalendarDateToUtc(d))}
            />
            {value.kind === 'custom' && (
              <Button variant="outline" size="sm" onClick={() => selectPreset('all')} className="mt-2">
                Clear
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default DateRangeFilter
